import { useTranslation } from 'react-i18next';
import { MagnifyingGlass, CaretDownIcon } from '@phosphor-icons/react';
import { UserCompanyRole } from '@monorepo/shared';
import { useState, useRef, useEffect } from 'react';

interface UsersFiltersProps {
  search: string;
  onSearchChange: (search: string) => void;
  roleFilter: string;
  onRoleFilterChange: (role: string) => void;
}

export const UsersFilters = ({
  search,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
}: UsersFiltersProps) => {
  const { t } = useTranslation();
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
    };

    if (isStatusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStatusDropdownOpen]);

  const getRoleLabel = (role: string) => {
    if (!role) return t('admin.users.filters.status.all');
    if (role === UserCompanyRole.SUPER_ADMIN) return t('admin.users.filters.status.super_admin');
    if (role === UserCompanyRole.SUPER_USER) return t('admin.users.filters.status.super_user');
    if (role === UserCompanyRole.VALIDATOR) return t('admin.users.filters.status.validator');
    if (role === UserCompanyRole.USER) return t('admin.users.filters.status.user');
    return role;
  };

  return (
    <div className="flex flex-col sm:flex-row gap-4 p-4 border-b border-gray-200">
      <div className="flex-1 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlass size={20} className="text-gray-400" />
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('admin.users.search.placeholder')}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
        />
      </div>

      <div className="flex gap-2">
        <div className="relative" ref={statusDropdownRef}>
          <button
            onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition whitespace-nowrap"
          >
            <span className="text-sm">{getRoleLabel(roleFilter)}</span>
            <CaretDownIcon size={16} className="text-gray-500" />
          </button>

          {isStatusDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-20">
              <div className="py-1">
                <button
                  onClick={() => {
                    onRoleFilterChange('');
                    setIsStatusDropdownOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                    !roleFilter ? 'bg-primary bg-opacity-10 text-primary' : 'text-gray-700'
                  }`}
                >
                  {t('admin.users.filters.status.all')}
                </button>
                <button
                  onClick={() => {
                    onRoleFilterChange(UserCompanyRole.SUPER_ADMIN);
                    setIsStatusDropdownOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                    roleFilter === UserCompanyRole.SUPER_ADMIN
                      ? 'bg-primary bg-opacity-10 text-primary'
                      : 'text-gray-700'
                  }`}
                >
                  {t('admin.users.filters.status.super_admin')}
                </button>
                <button
                  onClick={() => {
                    onRoleFilterChange(UserCompanyRole.SUPER_USER);
                    setIsStatusDropdownOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                    roleFilter === UserCompanyRole.SUPER_USER
                      ? 'bg-primary bg-opacity-10 text-primary'
                      : 'text-gray-700'
                  }`}
                >
                  {t('admin.users.filters.status.super_user')}
                </button>
                <button
                  onClick={() => {
                    onRoleFilterChange(UserCompanyRole.VALIDATOR);
                    setIsStatusDropdownOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                    roleFilter === UserCompanyRole.VALIDATOR
                      ? 'bg-primary bg-opacity-10 text-primary'
                      : 'text-gray-700'
                  }`}
                >
                  {t('admin.users.filters.status.validator')}
                </button>
                <button
                  onClick={() => {
                    onRoleFilterChange(UserCompanyRole.USER);
                    setIsStatusDropdownOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                    roleFilter === UserCompanyRole.USER
                      ? 'bg-primary bg-opacity-10 text-primary'
                      : 'text-gray-700'
                  }`}
                >
                  {t('admin.users.filters.status.user')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
